import "./screens.css";
import { ScreenManager } from "./ScreenManager";
import { SplashScreen } from "./SplashScreen";
import { HomeScreen, type HomeAction } from "./HomeScreen";
import { PlayerSelectScreen, type PlayerSelection } from "./PlayerSelectScreen";
import { GameScreen, type MatchConfig } from "./GameScreen";
import { PlayerColor } from "../../core/types";
import { audio } from "./sound";
import { AdManager } from "../../systems/AdManager";
import { initY8, showY8MidgameAd } from "../../platform/y8";

export function createAmbience(): HTMLElement {
  const layer = document.createElement("div");
  for (let i = 0; i < 10; i++) {
    const b = document.createElement("span");
    b.className = "bokeh";
    const size = 24 + Math.random() * 90;
    b.style.cssText = `left:${Math.random() * 100}vw;bottom:-20vh;width:${size}px;height:${size}px;
      animation-duration:${16 + Math.random() * 18}s;animation-delay:${-Math.random() * 20}s;
      --drift:${(Math.random() * 12 - 6).toFixed(1)}vw`;
    layer.appendChild(b);
  }
  for (let i = 0; i < 26; i++) {
    const t = document.createElement("span");
    t.className = "twinkle";
    t.style.cssText = `left:${Math.random() * 100}vw;top:${Math.random() * 100}vh;
      animation-duration:${1.4 + Math.random() * 2.4}s;animation-delay:${-Math.random() * 3}s`;
    layer.appendChild(t);
  }
  return layer;
}

/** Seat order on the select screen maps to these board colors. */
const SEAT_COLORS: PlayerColor[] = [
  PlayerColor.GREEN,
  PlayerColor.RED,
  PlayerColor.YELLOW,
  PlayerColor.BLUE,
];

function buildMatchConfig(sel: PlayerSelection): MatchConfig | null {
  const colors: PlayerColor[] = [];
  const names: string[] = [];
  const isAI: boolean[] = [];

  sel.slots.slice(0, sel.count).forEach((slot, i) => {
    if (slot.kind === "nobody") return; // empty seat — not playing
    colors.push(SEAT_COLORS[i]);
    if (slot.kind === "human") {
      names.push(slot.name.trim() || `Human ${i + 1}`);
      isAI.push(false);
    } else {
      const cpuNumber = isAI.filter(Boolean).length + 1;
      names.push(`Computer ${cpuNumber}`);
      isAI.push(true);
    }
  });

  return colors.length >= 2 ? { colors, names, isAI } : null;
}

export function mountFrontend(root: HTMLElement): void {
  // Keep the HTML boot splash visible until the JS splash is fully
  // painted — then fade it out so there's never a blue/green gap.
  const boot = root.querySelector<HTMLElement>(".boot-splash");

  const ambience = createAmbience();
  root.appendChild(ambience);

  const stage = document.createElement("div");
  stage.className = "screen-stage";
  root.appendChild(stage);

  const screens = new ScreenManager(stage);

  // Button click sounds for the whole app (capture phase, one place).
  // The dice button is excluded — it plays its own dice-roll sound.
  stage.addEventListener(
    "click",
    (e) => {
      const btn = (e.target as HTMLElement).closest?.("button");
      if (btn && !btn.classList.contains("dice-lg")) audio.play("click");
    },
    true,
  );

  const toast = (msg: string): void => {
    const el = document.createElement("div");
    el.className = "toast";
    el.textContent = msg;
    stage.appendChild(el);
    window.setTimeout(() => el.remove(), 2000);
  };

  /** Swallows double-fires (fast taps) so navigation never races itself. */
  let lastNav = 0;
  const once = <A extends unknown[]>(fn: (...args: A) => void) =>
    (...args: A): void => {
      const now = Date.now();
      if (now - lastNav < 400) return;
      lastNav = now;
      fn(...args);
    };

  const startMatch = async (selection: PlayerSelection): Promise<void> => {
    const cfg = buildMatchConfig(selection);
    if (!cfg) {
      toast("Need at least 2 players");
      return;
    }
    // Ads are platform-isolated by design:
    // - Y8 web build → Y8 interstitial (AdManager below no-ops, not native).
    // - Play Store → AdMob interstitial (Y8 call above no-ops, not Y8).
    // Offline / capped / blocked → both resolve fast, board starts instantly.
    audio.pauseForAd();
    try {
      if (!(await showY8MidgameAd())) {
        await AdManager.showInterstitialOnPlay();
      }
    } catch {} finally {
      audio.resumeAfterAd(false); // clear duck flag, stay paused — board is silent
    }
    void AdManager.hideBanner();
    audio.stopMusic(); // board is silent by design — music is menu-only
    screens.replace(new GameScreen(cfg, {
      onExit: once(() => {
        audio.startMusicLoop(2, 26); // back to the home screen groove
        screens.go("home");
        void AdManager.showHomeBanner();
      }),
      toast,
    }));
    audio.play("start");
    screens.go("game");
  };

  const openSelect = once((vsComputer: boolean): void => {
    const select = new PlayerSelectScreen(
      once(() => screens.go("home")),
      startMatch,
      vsComputer,
    );
    screens.replace(select);
    screens.go("select");
  });

  const splash = new SplashScreen();
  const home = new HomeScreen((action: HomeAction) => {
    if (action === "computer") openSelect(true);
    else if (action === "local") openSelect(false);
    else if (action === "exit") exitApp();
  });

  /** Watch the splash progress and continue to home when it finishes. */
  const watchSplash = (): void => {
    const poll = window.setInterval(() => {
      if (splash.done) {
        window.clearInterval(poll);
        window.setTimeout(() => screens.go("home"), 150);
      }
    }, 120);
  };

  /**
   * Exit that actually CLOSES the app: on Android the Capacitor App plugin
   * finishes the activity; in browsers we try window.close() and fall back
   * to a fresh splash → home loop instead of nothing.
   */
  const exitApp = once((): void => {
    audio.play("back");
    audio.stopMusic();
    void (async () => {
      try {
        const { App } = await import("@capacitor/app");
        await App.exitApp(); // native: ends the activity — never returns
        window.close();
      } catch {
        window.close(); // web fallback
      }
      window.setTimeout(() => {
        audio.startMusicLoop(2, 26);
        screens.replace(splash);
        screens.go("splash");
        watchSplash();
      }, 200);
    })();
  });

  screens.register(splash);
  screens.register(home);
  screens.go("splash");
  watchSplash();

  // Ads: banner only on Home bottom, interstitial before game.
  // Music: menu screens only — the board stays silent by design.
  // Ambience particles pause during the match (frees the GPU for the dice).
  // Y8 init is web-only and no-ops inside the native Play Store app.
  void initY8();
  void AdManager.init();
  screens.onChange = (id) => {
    ambience.style.display = id === "game" ? "none" : "";
    if (id === "home") {
      void AdManager.showHomeBanner().catch(() => {});
      audio.startMusicLoop(2, 26);
    } else if (id === "game") {
      void AdManager.hideBanner().catch(() => {});
      audio.stopMusic();
    } else {
      void AdManager.hideBanner().catch(() => {});
      audio.startMusicLoop(2, 26);
    }
  };

  // Boot splash was painting alone before JS; now the JS splash is
  // underneath it (same visuals). Fade the boot layer out after the
  // JS splash's 320ms is-active transition so the user perceives a
  // single continuous loading screen with no blue flash.
  if (boot) {
    window.setTimeout(() => {
      boot.style.transition = "opacity 280ms ease";
      boot.style.opacity = "0";
      window.setTimeout(() => boot.remove(), 320);
    }, 340);
  }

  // Minimized / another app opened: silence everything. Back: resume
  // audio — music only on the menu screens, never over the board.
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      audio.suspend();
    } else {
      audio.resume();
      if (screens.active !== "game") audio.startMusicLoop(2, 26);
    }
  });
}
